// vg-binding: P7.D-102
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/features/onboarding/steps/StepKYCDocs.tsx
//
// Phase 7 Wave 4 Task 10 — Step 2 of the onboarding wizard.
// Phase 7 Wave 18 Task 78 batch C6 (Amendment #1) — i18n rewrite.
//
// 4 OPTIONAL document slots (business_license, id_card
// tax_certificate, bank_proof). Each slot owns one file via the Task 08
// upload endpoint (multipart). Uploaded docs land in
// `useVendorWizardStore.kyc_doc_ids[]` so the Step 5 submit hand-off
// already has the ids cached.

import { useEffect, useState } from 'react';

import { Button } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import {
  type KycUploadResult,
  useDeleteKycDocument,
  useOnboardingStatus,
  useUploadKycDocument,
} from '../api.js';
import { useVendorWizardStore } from '../wizardStore.js';

type DocType = KycUploadResult['doc_type'];

interface SlotMeta {
  type: DocType;
  labelKey: string;
  hintKey: string;
}

const SLOTS: readonly SlotMeta[] = [
  {
    type: 'business_license',
    labelKey: 'step.kyc.slot.businessLicense.label',
    hintKey: 'step.kyc.slot.businessLicense.hint',
  },
  {
    type: 'id_card',
    labelKey: 'step.kyc.slot.idCard.label',
    hintKey: 'step.kyc.slot.idCard.hint',
  },
  {
    type: 'tax_certificate',
    labelKey: 'step.kyc.slot.taxCertificate.label',
    hintKey: 'step.kyc.slot.taxCertificate.hint',
  },
  {
    type: 'bank_proof',
    labelKey: 'step.kyc.slot.bankProof.label',
    hintKey: 'step.kyc.slot.bankProof.hint',
  },
];

const ACCEPTED_MIME = '.pdf,.jpg,.jpeg,.png';

/**
 * Server doc_type → wizard slot. The upload endpoint translates the wizard's
 * `tax_certificate` to the admin-side `tax_cert` on write, so the read path has
 * to translate back. Both spellings are accepted for safety.
 */
const WIRE_TO_SLOT: Record<string, DocType | undefined> = {
  business_license: 'business_license',
  id_card: 'id_card',
  identity: 'id_card',
  tax_cert: 'tax_certificate',
  tax_certificate: 'tax_certificate',
  bank_proof: 'bank_proof',
};

export function StepKYCDocs(): JSX.Element {
  const { t } = useTranslation('onboarding');
  const setKycDocIds = useVendorWizardStore((s) => s.setKycDocIds);
  const uploadM = useUploadKycDocument();
  const deleteM = useDeleteKycDocument();

  // Per-slot upload metadata so we can show "✓ uploaded" + filename.
  const [slotState, setSlotState] = useState<
    Partial<Record<DocType, { doc_id: string; file_url: string; file_name: string }>>
  >({});

  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // dbg-fixbug (2026-08-26): rehydrate from the server. Previously the uploaded
  // documents lived ONLY in `slotState`, so refreshing the page made them
  // disappear even though they were stored — and there was no way to review
  // what had already been sent. `useOnboardingStatus` now echoes them back.
  const statusQuery = useOnboardingStatus();
  const serverDocs = statusQuery.data?.kyc_documents;
  useEffect(() => {
    if (!serverDocs || serverDocs.length === 0) return;
    setSlotState((prev) => {
      const next = { ...prev };
      // Server sorts newest-first, so the first hit per slot wins.
      for (const doc of serverDocs) {
        const slot = WIRE_TO_SLOT[doc.doc_type];
        if (!slot || next[slot]) continue;
        next[slot] = {
          doc_id: doc.id,
          file_url: doc.file_url,
          file_name: doc.original_name || doc.file_url.split('/').pop() || '',
        };
      }
      return next;
    });
  }, [serverDocs]);

  /**
   * dbg-20260904 — the id set must MIRROR the current slots, not accumulate.
   *
   * This used to be `for (const doc of serverDocs) addKycDoc(doc.id)`, which was
   * the bug behind "Đã tải lên: N tài liệu" climbing without bound:
   *
   *   - the server returns EVERY kyc_documents row for the vendor, newest-first
   *     and un-collapsed, and re-uploading a slot inserts a new row rather than
   *     replacing the old one, so the list grows by one per re-upload;
   *   - `addKycDoc` de-dupes by id, which those distinct rows defeat;
   *   - the store is localStorage-persisted and nothing in the app ever resets
   *     it, so the total also survived reloads, logout, and vendor switches —
   *     a refresh made it worse rather than fixing it.
   *
   * `slotState` is already collapsed to at most one doc per slot (the loop above
   * keeps the newest), and it is exactly what the vendor sees on screen. Driving
   * the store from it keeps the count honest AND makes submit carry the four
   * current ids instead of every historical one.
   */
  /** Slots actually filled — bounded by the number of document types. */
  const uploadedCount = Object.values(slotState).filter((v) => v != null).length;

  const slotDocIdKey = Object.values(slotState)
    .map((v) => v?.doc_id ?? '')
    .filter((x) => x !== '')
    .sort()
    .join(',');

  useEffect(() => {
    setKycDocIds(slotDocIdKey === '' ? [] : slotDocIdKey.split(','));
    // Keyed on the joined string, not the object, so this settles instead of
    // re-running on every render with a fresh `slotState` reference.
  }, [slotDocIdKey, setKycDocIds]);

  async function handleFile(slot: DocType, file: File): Promise<void> {
    setErrorBanner(null);
    try {
      const result = await uploadM.mutateAsync({ file, doc_type: slot });
      // The store is reconciled from `slotState` by the effect above, so
      // writing the id here too would only add a transient entry (this runs
      // before the slot is set) and re-introduce a second source of truth.
      setSlotState((prev) => ({
        ...prev,
        [slot]: {
          doc_id: result.doc_id,
          file_url: result.file_url,
          file_name: file.name,
        },
      }));
    } catch (err) {
      const msg =
        err instanceof Error
          ? (err as Error & { user_message?: string }).user_message ?? err.message
          : t('step.kyc.uploadFailed');
      setErrorBanner(msg);
    }
  }

  async function handleRemove(slot: DocType): Promise<void> {
    const entry = slotState[slot];
    if (!entry) return;
    setErrorBanner(null);
    try {
      await deleteM.mutateAsync(entry.doc_id);
      // Likewise: clearing the slot below is what drops the id from the store.
      setSlotState((prev) => {
        const next = { ...prev };
        delete next[slot];
        return next;
      });
    } catch (err) {
      const msg =
        err instanceof Error
          ? (err as Error & { user_message?: string }).user_message ?? err.message
          : t('step.kyc.deleteFailed');
      setErrorBanner(msg);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-pw-text">{t('step.kyc.heading')}</h2>
      <p className="text-sm text-pw-text-muted">
        {t('step.kyc.description')}
      </p>

      {errorBanner ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorBanner}
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {SLOTS.map((slot) => {
          const entry = slotState[slot.type];
          return (
            <div
              key={slot.type}
              className="flex items-start justify-between gap-3 rounded-lg border border-pw-border bg-pw-surface p-4"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-pw-text">{t(slot.labelKey)}</p>
                <p className="mt-0.5 truncate text-xs text-pw-text-muted">
                  {t(slot.hintKey)}
                </p>
                {entry ? (
                  // dbg-fixbug (2026-08-26): the filename used to be plain text
                  // with no way to check what was actually sent. Link it to the
                  // (absolute) file URL so the vendor can review the upload.
                  <a
                    href={entry.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`onboarding-kyc-view-${slot.type}`}
                    className="mt-1 block truncate text-xs text-pw-primary-strong underline underline-offset-2 hover:opacity-80"
                    title={entry.file_name}
                  >
                    ✓ {entry.file_name} · {t('step.kyc.view')}
                  </a>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {entry ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    onClick={() => void handleRemove(slot.type)}
                    className="text-pw-text-muted hover:bg-red-50 hover:text-red-700"
                    disabled={deleteM.isPending}
                  >
                    {t('step.kyc.remove')}
                  </Button>
                ) : (
                  <label className="cursor-pointer rounded-md bg-pw-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-pw-primary-strong">
                    {uploadM.isPending ? t('step.kyc.uploading') : t('step.kyc.upload')}
                    <input
                      type="file"
                      data-testid="onboarding-kyc-docs-upload"
                      accept={ACCEPTED_MIME}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void handleFile(slot.type, file);
                        e.target.value = ''; // reset so re-selecting same file fires
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-pw-text-muted">
        {/* Counts the slots actually filled — the same rows rendered above.
            `kyc_doc_ids` was the wrong source: it is a persisted union of every
            id the server ever returned. See the reconcile effect. */}
        {t('step.kyc.uploadedSummary', { count: uploadedCount })}
      </p>
    </div>
  );
}

export default StepKYCDocs;
